/**
 * Owner Calendar (Feb 27 2026 — Truck Calendar Phase 1A finisher).
 *
 * `/calendar` — the owner's at-a-glance ops view of every upcoming event
 * across all sources (one-off daily posts, weekly schedule entries,
 * GCal-synced events). Reads from `/api/trucks/mine` so it shows EVERY
 * event the owner has, regardless of visibility (public + staff + private).
 *
 * Designed as a clean read-first list grouped by day, with color-coded
 * visibility chips and full ops detail so the owner can scan their next
 * 4 weeks at a glance and click into the editor when they want to
 * change something.
 *
 * Phase 1A scope intentionally:
 *   ✓ List view grouped by day for the next ~30 days
 *   ✓ Visibility chips (Public / Staff / Private)
 *   ✓ Ops detail (event type, prep, staff_needed)
 *   ✓ "Connect Google/Outlook" entry-point (sync moved from navbar)
 *   ✓ Staff management — invite + revoke seats
 *   ✗ Month grid view (visual polish, deferred)
 *   ✗ Drag to reschedule (Phase 1C)
 *   ✗ Per-event edit drawer (link out to dashboard for now)
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import api from "../lib/api";
import { fmtET, etTimeMs, etDateKey, etDateKeyFromMs } from "../lib/etTime";
import {
  Calendar as CalendarIcon, MapPin, Users, Hamburger, Plus, Trash, CopySimple,
  GoogleLogo, MicrosoftOutlookLogo, ListChecks, CaretRight, EnvelopeSimple,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import EventEditDrawer from "../components/EventEditDrawer";

const VIS = {
  public:  { label: "Public",  emoji: "🌐", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  staff:   { label: "Staff",   emoji: "👥", cls: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  private: { label: "Private", emoji: "🔒", cls: "bg-slate-100 text-slate-700 border-slate-300" },
};
const TYPE_LABEL = {
  street: "Street", festival: "Festival", catering: "Catering",
  private_party: "Private", popup: "Pop-up", farmers_market: "Market",
  brewery: "Brewery", corporate: "Corporate", other: "Other",
};

function fmtTime(starts, ends) {
  const sTime = fmtET(starts, { hour: "numeric", minute: "2-digit" });
  const eTime = ends ? fmtET(ends, { hour: "numeric", minute: "2-digit" }) : "";
  return eTime ? `${sTime} – ${eTime}` : sTime;
}

function groupByDay(events) {
  const nowMs = Date.now();
  const todayKey = etDateKeyFromMs(nowMs);
  const horizonKey = etDateKeyFromMs(nowMs, 30);
  const buckets = new Map();
  events
    .filter((e) => {
      const k = etDateKey(e.starts_at);
      return k && k >= todayKey && k <= horizonKey;
    })
    .sort((a, b) => etTimeMs(a.starts_at) - etTimeMs(b.starts_at))
    .forEach((e) => {
      const key = fmtET(e.starts_at, { weekday: "long", month: "long", day: "numeric" });
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(e);
    });
  return Array.from(buckets.entries());
}

function StaffManager() {
  const [invites, setInvites] = useState([]);
  const [creating, setCreating] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/owner/staff");
      setInvites(data || []);
    } catch (e) {
      // swallow — empty state is acceptable
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Give the crew member a name (or label) first.");
      return;
    }
    const emailTrimmed = email.trim();
    setCreating(true);
    try {
      const payload = { name: trimmed };
      if (emailTrimmed) payload.email = emailTrimmed;
      const { data } = await api.post("/owner/staff", payload);
      setName("");
      setEmail("");
      if (emailTrimmed && data?.email_sent) {
        toast.success(`Invite emailed to ${emailTrimmed}`);
      } else if (emailTrimmed) {
        toast.warning("Invite created, but email delivery failed — copy the link instead.");
      } else {
        toast.success("Invite link created — copy and share with your crew");
      }
      refresh();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        toast.error(detail || "Pro required for additional staff seats");
      } else {
        toast.error(detail || "Couldn't create invite");
      }
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite link? The staff member won't be able to access the schedule anymore.")) return;
    try {
      await api.delete(`/owner/staff/${id}`);
      toast.success("Invite revoked");
      refresh();
    } catch (e) {
      toast.error("Couldn't revoke");
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — share with your crew");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const sendEmail = async (inv) => {
    // If the invite has no email stored, prompt for one. Otherwise resend
    // to the stored address.
    let target = inv.email;
    if (!target) {
      target = window.prompt(`Send invite link to ${inv.name} — enter their email:`, "");
      if (target == null) return; // canceled
      target = target.trim();
      if (!target) return;
    }
    setSendingId(inv.id);
    try {
      const { data } = await api.post(`/owner/staff/${inv.id}/send`, { email: target });
      if (data?.email_sent) {
        toast.success(`Invite emailed to ${target}`);
      } else {
        toast.error("Email send failed — try again or copy the link instead.");
      }
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Couldn't send invite email");
    } finally {
      setSendingId(null);
    }
  };

  const active = invites.filter((i) => i.status !== "revoked");

  return (
    <section
      className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4"
      data-testid="calendar-staff-section"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold flex items-center gap-2">
            <Users weight="duotone" className="h-5 w-5 text-primary" />
            Staff
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Invite crew to view this calendar (read-only). 1 seat free, more with Pro.
            Add an email and we&rsquo;ll send the link for you.
          </p>
        </div>
        <span className="text-xs text-muted-foreground" data-testid="calendar-staff-count">
          {active.length} active
        </span>
      </header>

      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Crew member name (e.g. Mike, Lead Sat)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 min-w-[180px] h-9 text-sm"
            data-testid="staff-invite-name"
            onKeyDown={(e) => e.key === "Enter" && create()}
          />
          <Input
            type="email"
            placeholder="Email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 min-w-[180px] h-9 text-sm"
            data-testid="staff-invite-email"
            onKeyDown={(e) => e.key === "Enter" && create()}
          />
          <Button
            size="sm"
            onClick={create}
            disabled={creating}
            className="gap-1"
            data-testid="staff-invite-create"
          >
            <Plus weight="bold" className="h-4 w-4" />
            {creating ? "Creating…" : "Invite"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Leave email blank to just generate a link you can text/share manually.
        </p>
      </div>

      {active.length > 0 && (
        <ul className="space-y-2" data-testid="staff-invite-list">
          {active.map((inv) => (
            <li
              key={inv.id}
              className="rounded-xl border border-border bg-background px-3 py-2 flex items-center justify-between gap-3"
              data-testid={`staff-invite-${inv.id}`}
            >
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{inv.name}</div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mt-0.5">
                  {inv.status === "accepted" ? "✅ Active" : "⏳ Pending"}
                  {inv.accepted_at ? ` · joined ${new Date(inv.accepted_at).toLocaleDateString()}` : ""}
                  {inv.email_sent ? ` · 📧 emailed` : ""}
                </div>
                {inv.email ? (
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {inv.email}
                  </div>
                ) : null}
              </div>
              <div className="flex gap-1 flex-none">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => sendEmail(inv)}
                  disabled={sendingId === inv.id}
                  title={inv.email ? "Resend invite by email" : "Send invite by email"}
                  data-testid={`staff-invite-send-${inv.id}`}
                >
                  <PaperPlaneTilt weight="duotone" className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => copy(inv.invite_url)}
                  title="Copy invite link"
                  data-testid={`staff-invite-copy-${inv.id}`}
                >
                  <CopySimple weight="duotone" className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => revoke(inv.id)}
                  title="Revoke invite"
                  data-testid={`staff-invite-revoke-${inv.id}`}
                >
                  <Trash weight="duotone" className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── GCal needs-fix inbox (Mar 2026) ───────────────────────────────────
// Synced events whose address Google gave us couldn't be geocoded (or was
// missing). They're NOT on the map yet — but they belong on the schedule,
// with an inline fix: correct the address → re-geocode → publish + lock a
// pin_override so the next sync keeps the fix.
function GcalNeedsFixCard({ row, onFixed }) {
  const [address, setAddress] = useState(row.address || "");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const missingTime = !row.event_start || !row.event_end;
  const dismiss = async () => {
    setRemoving(true);
    try {
      await api.delete(`/integrations/google/pending/${row.gcal_event_id}`);
      toast.success("Removed from the list.");
      onFixed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove it — try again.");
    } finally {
      setRemoving(false);
    }
  };
  const fix = async (e) => {
    e.preventDefault();
    if (!address.trim()) { toast.error("Enter the event address."); return; }
    setSaving(true);
    try {
      await api.post(`/integrations/google/pending/${row.gcal_event_id}/resolve`, { address: address.trim() });
      toast.success("Fixed — the event is now on your schedule and the map.");
      onFixed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't place that address — try street + city.");
    } finally {
      setSaving(false);
    }
  };
  const when = row.event_start
    ? fmtET(row.event_start, {
        weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      }) || "no date/time"
    : "no date/time";
  return (
    <div className="rounded-xl border border-amber-200 bg-white/70 p-3 space-y-2" data-testid={`gcal-fix-${row.gcal_event_id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{row.title || "(no title)"}</div>
          <div className="text-[11px] text-muted-foreground">
            {when}
            {row.address ? <> · Google address: <span className="italic">“{row.address}”</span></> : " · no address on the event"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-none">
          {row.html_link && (
            <a href={row.html_link} target="_blank" rel="noreferrer" className="text-[11px] text-primary underline">
              open in Google
            </a>
          )}
          <button
            type="button"
            onClick={dismiss}
            disabled={removing}
            className="text-[11px] text-muted-foreground hover:text-destructive underline disabled:opacity-50"
            title="Remove from this list (won't come back unless the event changes)"
            data-testid={`gcal-fix-dismiss-${row.gcal_event_id}`}
          >
            {removing ? "Removing…" : "Remove"}
          </button>
        </div>
      </div>
      {missingTime ? (
        <p className="text-[11px] text-amber-800">
          Missing start/end time — add the time in Google Calendar, then fix the address here.
        </p>
      ) : (
        <form onSubmit={fix} className="flex gap-2">
          <Input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Corrected address (street + city)"
            className="h-8 text-sm"
            data-testid={`gcal-fix-input-${row.gcal_event_id}`}
          />
          <Button type="submit" size="sm" disabled={saving} className="flex-none" data-testid={`gcal-fix-btn-${row.gcal_event_id}`}>
            {saving ? "Placing…" : "Fix & publish"}
          </Button>
        </form>
      )}
    </div>
  );
}


// ── Crew gate (Mar 2026) ──────────────────────────────────────────────
// Shown at /calendar to logged-out / non-owner visitors with no
// remembered staff link. Self-serve recovery: resend the schedule link
// to the invited email (privacy-safe backend), owner login, home link.
function CrewGate() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState("");

  const resend = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Enter the email address your invite was sent to.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/staff/resend-link", { email: trimmed });
      setSentMsg(data?.message || "If that email has an active crew invite, the schedule link is on its way.");
    } catch {
      toast.error("Couldn't send right now — try again in a minute.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="crew-gate">
      <div className="max-w-md w-full space-y-6 text-center py-12">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary">Calendar</div>
          <h1 className="text-2xl font-display font-bold mt-1">Looking for your crew schedule?</h1>
          <p className="text-sm text-muted-foreground mt-2 leading-snug">
            Your schedule opens from the link in your invite email. Lost it, or
            on a new device? Enter the email your invite was sent to and
            we&rsquo;ll resend the link.
          </p>
        </div>
        {sentMsg ? (
          <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="crew-gate-sent">
            ✅ {sentMsg} Check your inbox, then open the link on this device.
          </div>
        ) : (
          <form onSubmit={resend} className="flex gap-2" data-testid="crew-gate-form">
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="crew-gate-email-input"
            />
            <Button type="submit" disabled={sending} data-testid="crew-gate-resend-btn" className="flex-none">
              {sending ? "Sending…" : "Resend link"}
            </Button>
          </form>
        )}
        <div className="border-t border-border pt-5 space-y-2">
          <p className="text-xs text-muted-foreground">Truck owner planning your calendar?</p>
          <Link to="/login" data-testid="crew-gate-owner-login">
            <Button variant="outline" size="sm">Owner log in</Button>
          </Link>
        </div>
        <Link to="/" className="inline-block text-xs text-muted-foreground hover:text-foreground underline" data-testid="crew-gate-home-link">
          ← Back to TruckTrackr home
        </Link>
      </div>
    </div>
  );
}


export default function CalendarView() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Crew routing gate (Mar 2026) ────────────────────────────────────
  // Non-owners landing on /calendar get routed to their crew schedule:
  //   1. Device-remembered staff link (localStorage) → redirect.
  //   2. Logged-in with an account whose email matches a staff invite
  //      (auto-linked at login) → redirect. THIS is the cross-device
  //      path: any browser/PC they log into finds the schedule.
  //   3. Otherwise → CrewGate recovery panel (resend link by email).
  // Owners always see their planning calendar. `null` = still deciding.
  const [crewGate, setCrewGate] = useState(null);
  useEffect(() => {
    if (authLoading) return;
    if (user?.role === "owner") { setCrewGate(false); return; }
    let token = "";
    try { token = localStorage.getItem("tt_staff_token") || ""; } catch { /* private browsing */ }
    if (token) { navigate(`/staff/${token}`, { replace: true }); return; }
    if (!user) { setCrewGate(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/staff-invites");
        if (cancelled) return;
        const inv = (data || []).filter((i) => i.status !== "revoked");
        if (inv[0]?.token) navigate(`/staff/${inv[0].token}`, { replace: true });
        else setCrewGate(true);
      } catch {
        if (!cancelled) setCrewGate(true);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user?.id, user?.role, navigate]);
  // Selected event for the slide-out edit drawer. `null` = closed.
  const [editingEvent, setEditingEvent] = useState(null);
  // Phase 1D — pending assignment digest queue.
  const [pending, setPending] = useState({ total_events: 0, total_recipients: 0, groups: [] });
  const [flushing, setFlushing] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await api.get("/trucks/mine");
      setEvents(data || []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPending = useCallback(async () => {
    try {
      const { data } = await api.get("/owner/staff/assignments/pending");
      setPending(data || { total_events: 0, total_recipients: 0, groups: [] });
    } catch {
      setPending({ total_events: 0, total_recipients: 0, groups: [] });
    }
  }, []);

  // Needs-fix synced events (Mar 2026) — GCal events whose address
  // couldn't be resolved. Previously invisible in-app (fixable only by
  // editing Google Calendar). Now listed here with an inline fix form.
  const [gcalPending, setGcalPending] = useState([]);
  const loadGcalPending = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/google/pending");
      setGcalPending(data?.pending || []);
    } catch {
      setGcalPending([]);
    }
  }, []);

  useEffect(() => {
    loadEvents();
    loadPending();
    loadGcalPending();
  }, [loadEvents, loadPending, loadGcalPending]);

  const handleSaved = useCallback(() => {
    // Refresh the list after a save so prep/crew/visibility/staff
    // chips reflect the new values without a full page reload.
    // ALSO refresh the pending queue — every newly-added assignee
    // adds a row, so the banner counter needs to bump immediately.
    loadEvents();
    loadPending();
  }, [loadEvents, loadPending]);

  const flushNow = async () => {
    setFlushing(true);
    try {
      const { data } = await api.post("/owner/staff/assignments/flush", {});
      const n = data?.emails_sent || 0;
      const ev = data?.events_flushed || 0;
      if (n === 0 && ev === 0) {
        toast.info("Nothing to send — queue is empty.");
      } else {
        toast.success(`Sent ${n} digest email${n === 1 ? "" : "s"} covering ${ev} shift${ev === 1 ? "" : "s"}.`);
      }
      loadPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send. Try again in a moment.");
    } finally {
      setFlushing(false);
    }
  };

  const grouped = groupByDay(events);

  // Render gate: null = auth/invite check still resolving (blank beat
  // beats flashing the wrong surface); true = CrewGate recovery panel.
  if (crewGate === null && user?.role !== "owner") {
    return <div className="min-h-screen bg-background" data-testid="calendar-resolving" />;
  }
  if (crewGate) {
    return <CrewGate />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="calendar-view">
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary">
              Operations
            </div>
            <h1 className="text-2xl font-display font-bold">Calendar</h1>
            <p className="text-xs text-muted-foreground mt-1">
              Every event for the next 30 days — public, staff-only, and private.
              <strong className="font-semibold text-foreground"> Tap any event</strong> to edit it.
            </p>
          </div>
          <Link to="/calendar-sync" data-testid="calendar-sync-link">
            <Button variant="outline" size="sm" className="gap-1.5">
              <GoogleLogo weight="duotone" className="h-4 w-4" />
              <MicrosoftOutlookLogo weight="duotone" className="h-4 w-4" />
              Connect calendar
            </Button>
          </Link>
        </header>

        {pending.total_events > 0 && (
          <div
            className="rounded-2xl border-2 border-amber-300 bg-amber-50/80 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
            data-testid="pending-assignments-banner"
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                <PaperPlaneTilt weight="duotone" className="h-4 w-4" />
                {pending.total_events} pending invite{pending.total_events === 1 ? "" : "s"} queued
              </div>
              <div className="text-[11px] text-amber-800/80 mt-0.5">
                Batching into {pending.total_recipients} digest email{pending.total_recipients === 1 ? "" : "s"}
                {" · "}auto-sends in ≤10 min, or send now to skip the wait.
              </div>
            </div>
            <Button
              size="sm"
              onClick={flushNow}
              disabled={flushing}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="pending-assignments-flush"
            >
              {flushing ? "Sending…" : "Send all now"}
            </Button>
          </div>
        )}

        <StaffManager />

        {gcalPending.length > 0 && (
          <div
            className="rounded-2xl border-2 border-amber-300 bg-amber-50/70 p-4 space-y-3"
            data-testid="gcal-needs-fix-section"
          >
            <div>
              <div className="text-sm font-semibold text-amber-900">
                📍 {gcalPending.length} synced event{gcalPending.length === 1 ? "" : "s"} need{gcalPending.length === 1 ? "s" : ""} a location fix
              </div>
              <div className="text-[11px] text-amber-800/80 mt-0.5">
                These came from your calendar but the address couldn&rsquo;t be placed on the
                map. Fix the address here — no need to edit Google Calendar.
              </div>
            </div>
            {gcalPending.map((row) => (
              <GcalNeedsFixCard
                key={row.gcal_event_id}
                row={row}
                onFixed={() => { loadGcalPending(); loadEvents(); }}
              />
            ))}
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : grouped.length === 0 ? (
          <div
            className="rounded-2xl border border-dashed border-border p-10 text-center"
            data-testid="calendar-empty"
          >
            <CalendarIcon weight="duotone" className="h-10 w-10 mx-auto mb-2 text-muted-foreground/60" />
            <p className="text-sm font-medium">No events in the next 30 days</p>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              Add a stop on your Dashboard to see it here.
            </p>
            <Link to="/dashboard">
              <Button size="sm" className="gap-1">
                <Plus weight="bold" className="h-4 w-4" />
                Go to Dashboard
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-5" data-testid="calendar-days">
            {grouped.map(([day, dayEvents]) => (
              <section key={day} data-testid={`calendar-day-${day.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                  {day}
                </h2>
                <div className="space-y-2">
                  {dayEvents.map((e) => {
                    const vis = VIS[e.visibility || "public"] || VIS.public;
                    return (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => setEditingEvent(e)}
                        data-testid={`calendar-event-${e.id}`}
                        className="block w-full text-left focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-xl"
                      >
                      <article
                        className={
                          "rounded-xl border bg-card p-3 sm:p-4 transition-colors hover:border-primary/50 " +
                          (e.is_canceled ? "border-rose-300 bg-rose-50/40" : "border-border")
                        }
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5 ${vis.cls}`}>
                                {vis.emoji} {vis.label}
                              </span>
                              {e.source === "google_calendar" && (
                                <span className="text-[10px] rounded-md bg-primary/10 text-primary px-1.5 py-0.5 font-bold uppercase tracking-wider">
                                  📅 GCal
                                </span>
                              )}
                              {e.event_type && (
                                <span className="text-[10px] rounded-md bg-muted/60 px-1.5 py-0.5 font-medium uppercase tracking-wider">
                                  {TYPE_LABEL[e.event_type] || e.event_type}
                                </span>
                              )}
                              {e.is_canceled && (
                                <span className="text-[10px] rounded-md bg-rose-100 text-rose-700 px-1.5 py-0.5 font-bold uppercase tracking-wider">
                                  🚫 Canceled
                                </span>
                              )}
                            </div>
                            <div className={`mt-1 font-semibold text-sm ${e.is_canceled ? "line-through text-rose-700/80" : ""}`}>
                              {e.event_name || e.truck_name || "Untitled stop"}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {fmtTime(e.starts_at, e.ends_at)}
                              {e.address ? ` · ${e.address}` : ""}
                            </div>
                            {(e.prep_count != null || e.staff_needed != null || (e.assigned_staff_invite_ids || []).length > 0) && (
                              <div className="flex flex-wrap gap-1.5 mt-1.5 text-[11px]">
                                {e.prep_count != null && (
                                  <span className="rounded-md bg-amber-100 text-amber-800 px-1.5 py-0.5 inline-flex items-center gap-1 font-medium">
                                    <Hamburger weight="fill" className="h-3 w-3" />
                                    Prep {e.prep_count}
                                  </span>
                                )}
                                {e.staff_needed != null && (
                                  <span className="rounded-md bg-indigo-100 text-indigo-800 px-1.5 py-0.5 inline-flex items-center gap-1 font-medium">
                                    <Users weight="fill" className="h-3 w-3" />
                                    {e.staff_needed} crew
                                  </span>
                                )}
                                {((e.assigned_staff_invite_ids || []).length > 0 || e.owner_on_shift !== false) && (
                                  <span
                                    className="rounded-md bg-emerald-100 text-emerald-800 px-1.5 py-0.5 inline-flex items-center gap-1 font-medium"
                                    data-testid={`calendar-event-assigned-${e.id}`}
                                  >
                                    👥 {(e.assigned_staff_invite_ids || []).length + (e.owner_on_shift !== false ? 1 : 0)} assigned
                                  </span>
                                )}
                                {(() => {
                                  // RSVP rollup chip — only shown when we
                                  // actually have RSVPs back from staff.
                                  // Renders as "✅ 2 · ❌ 1 · ⏳ 1" so the
                                  // owner can scan crew readiness at a
                                  // glance without opening the drawer.
                                  const rsvps = e.rsvps || {};
                                  const assigned = e.assigned_staff_invite_ids || [];
                                  if (assigned.length === 0) return null;
                                  let going = 0, declined = 0;
                                  assigned.forEach((id) => {
                                    const r = rsvps[id]?.response;
                                    if (r === "going") going++;
                                    else if (r === "declined") declined++;
                                  });
                                  const pending = assigned.length - going - declined;
                                  if (going === 0 && declined === 0) return null;
                                  return (
                                    <span
                                      className="rounded-md bg-white border border-border px-1.5 py-0.5 inline-flex items-center gap-1.5 font-medium"
                                      data-testid={`calendar-event-rsvp-${e.id}`}
                                    >
                                      {going > 0 && <span className="text-emerald-700">✅ {going}</span>}
                                      {declined > 0 && <span className="text-rose-700">❌ {declined}</span>}
                                      {pending > 0 && <span className="text-muted-foreground">⏳ {pending}</span>}
                                    </span>
                                  );
                                })()}
                              </div>
                            )}
                            {e.prep_notes && (
                              <div className="mt-1.5 text-xs text-foreground/70 inline-flex items-start gap-1">
                                <ListChecks weight="duotone" className="h-3.5 w-3.5 mt-0.5 flex-none text-muted-foreground" />
                                <span className="line-clamp-1">{e.prep_notes}</span>
                              </div>
                            )}
                          </div>
                          <CaretRight weight="bold" className="h-4 w-4 text-muted-foreground/40 mt-1 flex-none" />
                        </div>
                      </article>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
      <EventEditDrawer
        open={!!editingEvent}
        onOpenChange={(o) => {
          if (!o) setEditingEvent(null);
        }}
        event={editingEvent}
        onSaved={handleSaved}
      />
    </div>
  );
}
